import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExtensionIcon from '@mui/icons-material/Extension';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LinkIcon from '@mui/icons-material/Link';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import InlineProgress from '../components/InlineProgress';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  fontWeight: 600,
}));

const DependencyChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.info.main,
  color: theme.palette.info.contrastText,
  fontWeight: 600,
  fontSize: '0.875rem',
  margin: theme.spacing(0.5),
}));

function DefineFullProductFeaturesStep({ data, updateWorkshop }) {
  const handleContinue = () => {
    updateWorkshop(data.id, { nextStep: 'generateWorkshopReport' });
  };

  return (
    <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        <ExtensionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Full Product Features
      </Typography>
      {data.nextStep === 'defineFullProductFeatures' ? (
        <InlineProgress message="Defining full product features..." />
      ) : data.fullProductFeatures ? (
        <>
          <AnimatePresence>
            <List>
              {data.fullProductFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <StyledPaper elevation={3} sx={{ mb: 3, p: 3 }}>
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', p: 0 }}>
                      <Typography variant="h6" gutterBottom>{feature.shortDescription}</Typography>
                      <ListItemText
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {feature.longDescription}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
                              <Typography variant="body2" color="text.primary">
                                <strong>Potential Impact:</strong> {feature.potentialImpact}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                              <LinkIcon color="action" sx={{ mr: 1 }} />
                              <Typography variant="body2" color="text.primary" sx={{ mr: 1 }}>
                                <strong>Dependencies:</strong>
                              </Typography>
                              {feature.dependencies.map((dep, i) => (
                                <DependencyChip key={i} label={dep} size="small" />
                              ))}
                            </Box>
                          </>
                        }
                      />
                    </ListItem>
                  </StyledPaper>
                </motion.div>
              ))}
            </List>
          </AnimatePresence>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleContinue}
              endIcon={<ArrowForwardIcon />}
            >
              Continue to Generate Workshop Report
            </StyledButton>
          </Box>
        </>
      ) : (
        <Typography variant="body1" color="text.secondary">
          No full product features defined yet.
        </Typography>
      )}
    </Box>
  );
}

export default DefineFullProductFeaturesStep;